import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetIDsFromURL } from 'components/DeviceDetails/helper';
import { useDeviceStats } from 'components/DeviceDetails/service';
import { Card, CardContent } from 'components/shadcn/ui/card';
import { Button } from 'components/shadcn/ui/button';
import { HGTable, TableSkeleton } from 'components/HGTable';
import HGStatus from 'components/HGStatus';

const MicroinvertersTable = () => {
    const { Thing_ID } = useGetIDsFromURL();
    const { data: stats, isLoading } = useDeviceStats(Thing_ID);
    const navigate = useNavigate();
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState(1);

    const latestInverterData = useMemo(() => {
        if (!stats) return null;
        // Iterate over the stats to get the latest data for each inverter
        return Object.keys(stats).map((device_sn) => {
            const deviceStats = stats[device_sn]; // Renamed variable to avoid shadowing
            const latestStat = deviceStats[deviceStats.length - 1]; // Assuming the latest stat is at the end
            return {
                ...latestStat, // Spread all properties from the latestStat
                device_sn, // Overwrite or add specific properties as needed
                status: latestStat.status === 0 ? 'online' : 'offline', // overriding original status number to a string of "online" or "offline" for use with Status component in HGTable
                type: 'Microinverter',
            };
        });
    }, [stats]);

    const columns = [
        {
            Header: 'Status',
            accessor: 'status',
            component: (item) => <HGStatus online={item.status} />,
        },
        {
            Header: 'Device Name',
            accessor: 'device_sn',
        },
        {
            Header: 'Device Type',
            accessor: 'type',
        },
        {
            Header: 'Power (Watts)',
            accessor: 'power_w',
        },
        {
            Header: 'Voltage DC (V)',
            accessor: 'vdc_v',
        },
        {
            Header: 'Voltage AC (V)',
            accessor: 'vac_v',
        },
        {
            Header: 'Frequency (Hz)',
            accessor: 'freq_hz',
        },
        {
            Header: 'Temperature (°C)',
            accessor: 'temp_c',
        },
    ];

    return (
        <Card>
            {isLoading ? (
                <div className="px-5 pt-5">
                    <TableSkeleton columns={columns} />
                </div>
            ) : !stats || latestInverterData?.length === 0 ? (
                <Card>
                    <CardContent className="mb-8 mt-12 flex flex-col items-center justify-center">
                        <h2 className="mb-4 text-center text-3xl font-bold opacity-20">
                            No Microinverters Available
                        </h2>
                        <p className="mb-6 w-3/4 text-center opacity-30 md:w-full">
                            Would you like to add microinverters?
                        </p>
                        <div className="flex justify-center">
                            <Button
                                variant="outline"
                                size="default"
                                onClick={() =>
                                    navigate(
                                        `/devices/${Thing_ID}/add-microinverters`
                                    )
                                }
                            >
                                Add microinverters
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <>
                    <div className="flex items-center justify-between px-5 pt-5">
                        <h4 className="text-lg font-semibold">Microinverters</h4>
                        <Button
                            variant="outline"
                            size="default"
                            onClick={() =>
                                navigate(`/devices/${Thing_ID}/edit-devices`)
                            }
                        >
                            add / edit
                        </Button>
                    </div>
                    <HGTable
                        columns={columns}
                        data={latestInverterData}
                        sortField={sortField}
                        setSortField={setSortField}
                        sortDirection={sortDirection}
                        setSortDirection={setSortDirection}
                        currentPage={1}
                        setCurrentPage={() => {}}
                        pageSize={latestInverterData.length}
                        totalItems={latestInverterData.length}
                    />
                </>
            )}
        </Card>
    );
};

export default MicroinvertersTable;
